/**
 * @name MenuExport
 * @description Componente para gestionar las opciones de exportar del reporte
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import styled from 'styled-components';

import { CSVLink } from 'react-csv';

// Material-UI
import MuiPopover from '@material-ui/core/Popover';
import MuiList from '@material-ui/core/List';
import MuiListItem from '@material-ui/core/ListItem';

import { Flex, MUI, Unicons, Typography } from '@styles';

class MenuExport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorMenu: null,
      headers: []
    };
  }

  componentDidMount() {
    let headers = [];
    if (this.props.exportToCSV) {
      headers = this.props.columns.map(({ name, header }) => ({ label: header, key: name, }));
    }
    this.setState({ headers });
  }

  openMenu = (event) => {
    this.setState({ anchorMenu: event.currentTarget });
  };

  closeMenu = () => {
    this.setState({ anchorMenu: null });
  };

  render() {
    const {
      rows,
      exportToCSV,
      exportAllToExcel,
      exportFiltersToExcel,
      exportFileName,
      onExportAllToExcel,
      onExportFiltersToExcel,
    } = this.props;

    return (
      <>
        <MUI.Tooltip title={i18n.t('Report.export')}>
          <MUI.ButtonIcon onClick={this.openMenu}>
            <Unicons.Export />
          </MUI.ButtonIcon>
        </MUI.Tooltip>

        <Popover
          open={Boolean(this.state.anchorMenu)}
          anchorEl={this.state.anchorMenu}
          onClose={this.closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Flex.Col padding="16px 16px 0">
            <Typography.H6>{i18n.t('Report.export')}</Typography.H6>
            <Typography.Small>{i18n.t('Report.selectExportOptions')}</Typography.Small>
          </Flex.Col>

          {(exportToCSV || exportAllToExcel || exportFiltersToExcel) && (
            <List component="nav" aria-label="export-list">
              {/* Export CSV */}
              {exportToCSV && (
                <StyledCSVLink
                  headers={this.state.headers}
                  data={rows}
                  filename={`${exportFileName}.csv`}
                >
                  <ListItem button>
                    <Typography.Text>{i18n.t('Report.exportToCsv')}</Typography.Text>
                  </ListItem>
                </StyledCSVLink>
              )}
              {/* Export All Excel */}
              {exportAllToExcel && (
                <ListItem button onClick={() => onExportAllToExcel()}>
                  <Typography.Text>{i18n.t('Report.exportAllToExcel')}</Typography.Text>
                </ListItem>
              )}
              {/* Export CSV */}
              {exportFiltersToExcel && (
                <ListItem button onClick={() => onExportFiltersToExcel()}>
                  <Typography.Text>{i18n.t('Report.exportFiltetredToExcel')}</Typography.Text>
                </ListItem>
              )}
            </List>
          )}
        </Popover>
      </>
    );
  }
}

MenuExport.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.array.isRequired,
  // Registros mostrados actualmente en el grid.
  rows: PropTypes.array.isRequired,
  // Boolean que muestra la opción de exportar a csv.
  exportToCSV: PropTypes.bool,
  // Boolean que muestra la opción de exportar a todos los registros a Excel.
  exportAllToExcel: PropTypes.bool,
  // Boolean que muestra la opción de exportar a los registros filtrados a Excel.
  exportFiltersToExcel: PropTypes.bool,
  // Nombre que se le da al archivo cuando se exportan los registros.
  exportFileName: PropTypes.string,
  // Función para exportar a todos los registros a Excel.
  onExportAllToExcel: PropTypes.func,
  // Función para exportar a los registros filtrados a Excel.
  onExportFiltersToExcel: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Popover = styled(MuiPopover)`
  .MuiPopover-paper {
    border-radius: 6px;
    border: 1px solid ${(props) => props.theme.colors.greyLight};
    box-shadow: ${(props) => props.theme.shadows.depth4};
  }
`;

const List = styled(MuiList)`
  &.MuiList-padding {
    padding-top: 8px;
    padding-bottom: 8px;
  }
`;

const ListItem = styled(MuiListItem)`
  &.MuiListItem-root {
    min-height: 40px;
    padding: 0px 16px;
  }

  & > a {
    text-decoration: none;
  }
`;

const StyledCSVLink = styled(CSVLink)`
  text-decoration: none;
`;

export default MenuExport;